import {
  completedTaskIdsForDate,
  getTodayDateStringInTimezone,
  reminderCompletedOnDate,
  reminderIsScheduledOnDate,
  reminderSkippedOnDate,
  reminderTasks,
  shiftDateString,
} from './reminderUtils';

export const REMINDER_HISTORY_MAX_DAYS = 93;

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const csvEscape = (value) => {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const xmlEscape = (value) => String(value == null ? '' : value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

export function listDatesInRange(fromDate, toDate, maxDays = REMINDER_HISTORY_MAX_DAYS) {
  if (!fromDate || !toDate) return [];
  const start = fromDate <= toDate ? fromDate : toDate;
  const end = fromDate <= toDate ? toDate : fromDate;
  const dates = [];
  let cursor = start;
  while (cursor <= end && dates.length < maxDays) {
    dates.push(cursor);
    cursor = shiftDateString(cursor, 1);
  }
  return dates;
}

export function formatHistoryDateHeader(dateStr) {
  const [year, month, day] = String(dateStr).split('-').map(Number);
  if (!year || !month || !day) return dateStr;
  const utc = new Date(Date.UTC(year, month - 1, day));
  return utc.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function cellStatus(reminder, dateStr, task) {
  if (!reminderIsScheduledOnDate(reminder, dateStr)) return '';
  const todayStr = getTodayDateStringInTimezone(reminder.timezone);
  if (dateStr > todayStr) return 'Upcoming';
  if (reminderSkippedOnDate(reminder, dateStr)) return 'Missed';
  if (task?.id) {
    const doneIds = completedTaskIdsForDate(reminder, dateStr);
    if (doneIds.includes(task.id)) return 'Done';
    if (dateStr === todayStr) return 'Pending';
    return 'Missed';
  }
  if (reminderCompletedOnDate(reminder, dateStr)) return 'Done';
  if (dateStr === todayStr) return 'Pending';
  return 'Missed';
}

export function buildReminderHistoryReport(reminders, fromDate, toDate) {
  const dates = listDatesInRange(fromDate, toDate);
  const rows = [];
  (Array.isArray(reminders) ? reminders : []).forEach((reminder) => {
    const title = reminder?.title || '(No title)';
    const tasks = reminderTasks(reminder);
    if (!tasks.length) {
      rows.push({
        reminder: title,
        task: '',
        cells: dates.map((dateStr) => cellStatus(reminder, dateStr, null)),
      });
      return;
    }
    tasks.forEach((task) => {
      rows.push({
        reminder: title,
        task: task.title || 'Task',
        cells: dates.map((dateStr) => cellStatus(reminder, dateStr, task)),
      });
    });
  });
  return {
    fromDate: fromDate <= toDate ? fromDate : toDate,
    toDate: fromDate <= toDate ? toDate : fromDate,
    dates,
    headers: ['Reminder', 'Task', ...dates.map(formatHistoryDateHeader)],
    rows,
  };
}

export function reminderHistoryTableMatrix(report) {
  return [report.headers, ...report.rows.map((row) => [row.reminder, row.task, ...row.cells])];
}

export function downloadReminderHistoryCsv(report) {
  const matrix = reminderHistoryTableMatrix(report);
  const csv = matrix.map((row) => row.map(csvEscape).join(',')).join('\r\n');
  downloadBlob(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }), `reminder-history-${report.fromDate}-to-${report.toDate}.csv`);
}

export function downloadReminderHistoryExcel(report) {
  const matrix = reminderHistoryTableMatrix(report);
  const rowsXml = matrix.map((row) => {
    const cells = row.map((value) => `<Cell><Data ss:Type="String">${xmlEscape(value)}</Data></Cell>`).join('');
    return `<Row>${cells}</Row>`;
  }).join('');
  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Worksheet ss:Name="Reminder history">
  <Table>${rowsXml}</Table>
 </Worksheet>
</Workbook>`;
  downloadBlob(new Blob([xml], { type: 'application/vnd.ms-excel' }), `reminder-history-${report.fromDate}-to-${report.toDate}.xls`);
}

export function downloadReminderHistoryJson(report) {
  const payload = {
    fromDate: report.fromDate,
    toDate: report.toDate,
    dates: report.dates,
    rows: report.rows.map((row) => ({
      reminder: row.reminder,
      task: row.task,
      statusByDate: Object.fromEntries(report.dates.map((dateStr, index) => [dateStr, row.cells[index] || ''])),
    })),
  };
  downloadBlob(
    new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' }),
    `reminder-history-${report.fromDate}-to-${report.toDate}.json`,
  );
}

export function printReminderHistoryPdf(report) {
  const matrix = reminderHistoryTableMatrix(report);
  const header = matrix[0] || [];
  const body = matrix.slice(1);
  const tableHead = header.map((cell) => `<th>${xmlEscape(cell)}</th>`).join('');
  const tableBody = body.map((row) => `<tr>${row.map((cell) => `<td>${xmlEscape(cell)}</td>`).join('')}</tr>`).join('');
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Reminder history ${report.fromDate} to ${report.toDate}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
    h1 { font-size: 18px; margin: 0 0 8px; }
    p { font-size: 12px; color: #555; margin: 0 0 16px; }
    table { border-collapse: collapse; width: 100%; font-size: 11px; }
    th, td { border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; white-space: nowrap; }
    th { background: #f3f4f6; }
  </style>
</head>
<body>
  <h1>All reminder history</h1>
  <p>${xmlEscape(report.fromDate)} to ${xmlEscape(report.toDate)} · Generated ${xmlEscape(new Date().toLocaleString())}</p>
  <table>
    <thead><tr>${tableHead}</tr></thead>
    <tbody>${tableBody}</tbody>
  </table>
</body>
</html>`;
  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
  return true;
}
